/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  TimePicker,
} from 'antd';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import moment from 'moment';

function OnTraccrTimePicker(props) {
  const {
    date,
    timezone,
    format,
    value,
    onChange,
    minuteIncrement = 1,
    disabled,
    onOpenChange,
    isHourBasedObject = false,
  } = props;
  const startOfDay = useMemo(() => (
    DateTime.fromISO(date, { zone: timezone }).startOf('day')
  ), [date, timezone]);

  const momentValue = useMemo(() => {
    if (!value) return value;
    if (isHourBasedObject) {
      const { hours = 0, minutes = 0 } = value || {};
      const m = moment().startOf('day');
      m.hours(hours);
      m.minutes(minutes);

      // If the hours are not the same, add a day and set the hours
      // Corrects for daylight savings time
      if (m.hours() !== hours) {
        m.add(1, 'day');
        m.hours(hours);
      }
      return m;
    }
    const m = moment(value);
    const zoneValue = DateTime.fromMillis(value, { zone: timezone });
    m.hours(zoneValue.hour);
    m.minutes(zoneValue.minute);
    return m;
  }, [value, timezone, isHourBasedObject]);

  const onTimeChanged = useCallback((momentInstance) => {
    if (!momentInstance) return onChange(null);

    const hour = momentInstance.hours() ?? 0;
    const minute = momentInstance.minutes() ?? 0;
    if (isHourBasedObject) {
      return onChange({ hours: hour, minutes: minute });
    }
    return onChange(
      startOfDay.set({ hour, minute }).toMillis(),
    );
  }, [onChange, startOfDay, isHourBasedObject]);

  useEffect(() => {
    if (isHourBasedObject || !value) return;
    const valueDT = DateTime.fromMillis(value, { zone: timezone });
    if (valueDT.toSQLDate() !== startOfDay.toSQLDate()) {
      const newDate = startOfDay.set({
        hour: valueDT.hour,
        minute: valueDT.minute,
      });
      onChange(newDate.toMillis());
    }
  }, [onChange, startOfDay, value, timezone, isHourBasedObject]);

  return (
    <TimePicker
      {...props}
      format={format}
      showNow={false}
      value={momentValue}
      onChange={onTimeChanged}
      minuteStep={minuteIncrement}
      disabled={disabled}
      onOpenChange={onOpenChange ?? null}
    />
  );
}

OnTraccrTimePicker.propTypes = {
  date: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  format: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      hours: PropTypes.number,
      minutes: PropTypes.number,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  minuteIncrement: PropTypes.number,
  disabled: PropTypes.bool,
  onOpenChange: PropTypes.func,
  isHourBasedObject: PropTypes.bool,
};

OnTraccrTimePicker.defaultProps = {
  timezone: DateTime.local().zoneName,
  value: undefined,
  minuteIncrement: 1,
  disabled: false,
  onOpenChange: null,
  isHourBasedObject: false,
};

export default OnTraccrTimePicker;
